import { createContext, useContext } from "react";

/**
 * What a screen is allowed to know about authentication.
 *
 * Narrower than `CognitoAuth` on purpose: a page should be able to show who is
 * signed in and start or end a session, and should not be able to reach a
 * token. `ApiClient` is the only thing that needs one.
 *
 * `canSignOut` is false in the development token mode, where there is no
 * Cognito session to end and the button would be a lie.
 */
export interface AuthValue {
  readonly isSignedIn: boolean;
  readonly email?: string;
  readonly canSignOut: boolean;
  signIn(returnTo?: string): void;
  signOut(): void;
  /**
   * Called when a request comes back 401, or a token could not be produced.
   * Drops the session so the app falls back to the sign-in screen instead of
   * showing an error the user cannot act on.
   */
  reportSignedOut(): void;
}

const AuthContext = createContext<AuthValue | null>(null);

export const AuthProvider = AuthContext.Provider;

export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (value === null) {
    throw new Error("useAuth must be used inside AuthProvider");
  }
  return value;
}
